import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button, Calendar, Icon, Input } from "@elaniintc/goes-design-system";
import { useEffect, useState } from "react";
import {
  type MaskitoDateParams,
  maskitoDateOptionsGenerator,
  maskitoStringifyDate,
  maskitoParseDate,
} from "@maskito/kit";
import { useMaskito } from "@maskito/react";

const params: MaskitoDateParams = {
  mode: "dd/mm/yyyy",
  separator: "/",
};
const options = maskitoDateOptionsGenerator(params);

const MAX_YEAR_OFFSET = 20;

function isoUtcToLocalDate(iso?: string): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return undefined;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function localDateToIsoUtc(date?: Date): string | undefined {
  if (!date) return undefined;
  const localStart = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0,
  );
  return localStart.toISOString();
}

interface Props {
  value?: string;
  onChangeDate: (value: string | undefined) => void;
  className?: string;
  group?:
    | "down-left"
    | "down"
    | "down-right"
    | "left"
    | "right"
    | "up"
    | "up-left"
    | "up-right"
    | "single"
    | "middle"
    | "middle-right"
    | "middle-left";
}

const ComparativeValueInputCalendar = ({
  value,
  onChangeDate,
  group = "down",
  className,
}: Props) => {
  const [open, setOpen] = useState(false);

  const [date, setDate] = useState<Date | undefined>(undefined);

  const [dateValue, setDateValue] = useState<string>("");

  const maskedInputRef = useMaskito({ options });

  useEffect(() => {
    const localDate = isoUtcToLocalDate(value);
    if (localDate) {
      setDate(localDate);
      setDateValue(
        maskitoStringifyDate(localDate, { separator: "/", mode: "dd/mm/yyyy" }),
      );
    } else {
      setDate(undefined);
      setDateValue("");
    }
  }, [value]);

  return (
    <div className="w-full relative">
      <Input
        ref={maskedInputRef}
        group={group}
        className={`${className ?? ""} w-full`}
        value={dateValue}
        onBlur={() => {
          const parsedDate = maskitoParseDate(dateValue, params);
          if (!parsedDate && date) {
            setDateValue(
              maskitoStringifyDate(date, {
                separator: "/",
                mode: "dd/mm/yyyy",
              }),
            );
          }
        }}
        onChange={(e) => {
          const raw = e.target.value;
          const parsedDate = maskitoParseDate(raw, params);

          if (!parsedDate && raw.length === 0) {
            setDate(undefined);
            setDateValue("");
            onChangeDate(undefined);
            return;
          }

          if (
            parsedDate &&
            parsedDate.getFullYear() >
              new Date().getFullYear() + MAX_YEAR_OFFSET
          ) {
            if (date) {
              setDateValue(
                maskitoStringifyDate(date, {
                  separator: "/",
                  mode: "dd/mm/yyyy",
                }),
              );
            } else {
              setDateValue("");
            }
            return;
          }

          if (parsedDate) {
            setDate(parsedDate);
            const iso = localDateToIsoUtc(parsedDate);
            onChangeDate(iso);
          }
          setDateValue(raw);
        }}
        placeholder="Seleccionar fecha"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Seleccionar fecha"
            className="top-1.5 right-4 absolute"
          >
            <Icon type="far" name="calendar" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-full overflow-hidden p-0 bg-neutral-object border-neutral"
          align="end"
        >
          <Calendar
            mode="single"
            selected={date}
            captionLayout="dropdown"
            defaultMonth={date || new Date()}
            endMonth={
              new Date(new Date().getFullYear() + MAX_YEAR_OFFSET, 11, 31)
            }
            className="bg-transparent w-full max-w-full"
            onSelect={(selectedDate) => {
              setDate(selectedDate);
              const iso = localDateToIsoUtc(selectedDate || undefined);
              onChangeDate(iso);
              setOpen(false);
              if (selectedDate) {
                setDateValue(
                  maskitoStringifyDate(selectedDate, {
                    separator: "/",
                    mode: "dd/mm/yyyy",
                  }),
                );
              } else {
                setDateValue("");
              }
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ComparativeValueInputCalendar;
